'use client';
import { useState, useRef, useEffect, Fragment } from 'react';
import { useRouter } from 'next/navigation';
import { useAuthStore } from '@/store/auth.store';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import SpecialistCard from '@/components/specialists/SpecialistCard';
import SpecialistRow from '@/components/specialists/SpecialistRow';
import MobileFocusView from '@/components/specialists/MobileFocusView';
import NewRequestForm from '@/components/requests/NewRequestForm';
import { Search, X, ClipboardList, List, Map, SlidersHorizontal, Grid3x3, AlignJustify, GalleryHorizontal } from 'lucide-react';
import SpecialistsMap from '@/components/specialists/SpecialistsMap';
import { useCityStore } from '@/store/city.store';
import { SpecialistCardSkeleton } from '@/components/ui/Skeleton';
import CategoryChips from '@/components/catalog/CategoryChips';
import FiltersSheet from '@/components/catalog/FiltersSheet';

type SortValue = 'recommended' | 'rating' | 'experience';

// Сортировки «ближайшая запись» и «цена» из ТЗ здесь отсутствуют осознанно:
// nextSlotAt вычисляется на бэкенде уже после пагинации, а минимальная цена
// лежит в услугах и агрегатом не сортируется. Обе требуют денормализации.
const SORT_OPTIONS: { value: SortValue; label: string }[] = [
  { value: 'recommended', label: 'Рекомендуемые' },
  { value: 'rating', label: 'Рейтинг' },
  { value: 'experience', label: 'Опыт' },
];

type Filters = {
  city: string;
  category: string;
  date: string;
  q: string;
  rating_min: string;
  price_min: string;
  price_max: string;
  without_schedule: boolean;
  sort: SortValue;
};

const EMPTY_FILTERS: Filters = { city: '', category: '', date: '', q: '', rating_min: '', price_min: '', price_max: '', without_schedule: false, sort: 'recommended' };

// Режимы сокращены с шести до трёх (ТЗ 3.1): «Сетка 2 в ряд», «1 в ряд» и
// «Плитки» удалены — три из них содержали визуальные баги при пренебрежимой
// доле использования. «Фокус» оставлен: это дефолт мобильной витрины.
type CatalogView = 'grid3' | 'rows' | 'focus';
const CATALOG_VIEW_OPTIONS: { value: CatalogView; label: string; Icon: any; gridClass: string; mobileOk: boolean; desktopOk: boolean }[] = [
  { value: 'grid3', label: 'Сетка', Icon: Grid3x3, gridClass: 'sp-grid--3', mobileOk: true, desktopOk: true },
  { value: 'rows', label: 'Список', Icon: AlignJustify, gridClass: 'sp-grid--rows', mobileOk: true, desktopOk: true },
  { value: 'focus', label: 'Фокус', Icon: GalleryHorizontal, gridClass: 'sp-grid--1', mobileOk: true, desktopOk: false },
];
const DEFAULT_CATALOG_VIEW: CatalogView = 'grid3';

export default function HomePage() {
  const [search, setSearch] = useState<Filters>(EMPTY_FILTERS);
  const [qInput, setQInput] = useState('');
  const { city: selectedCity, cityLat, cityLng } = useCityStore();
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [catalogView, setCatalogView] = useState<CatalogView>(DEFAULT_CATALOG_VIEW);
  const [page, setPage] = useState(1);

  // Восстанавливаем вид каталога из localStorage.
  // У части пользователей там лежит удалённый режим (grid2/grid1/tiles) —
  // такое значение не проходит проверку и молча заменяется на сетку (ТЗ 3.1).
  useEffect(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
    try {
      const saved = localStorage.getItem('catalogView') as CatalogView | null;
      if (saved && CATALOG_VIEW_OPTIONS.some(o => o.value === saved)) {
        // 'focus' недоступен на десктопе — заменяем на сетку
        if (saved === 'focus' && !isMobile) { setCatalogView(DEFAULT_CATALOG_VIEW); return; }
        setCatalogView(saved);
        return;
      }
      if (saved) localStorage.setItem('catalogView', DEFAULT_CATALOG_VIEW);
    } catch {}
    // Нет сохранённого выбора: на мобильном по умолчанию — фокус-режим
    if (isMobile) setCatalogView('focus');
  }, []);
  const handleCatalogViewChange = (v: CatalogView) => {
    setCatalogView(v);
    try { localStorage.setItem('catalogView', v); } catch {}
  };
  const [allSpecialists, setAllSpecialists] = useState<any[]>([]);

  // Синхронизируем город из store с фильтром
  useEffect(() => {
    setSearch(prev => (prev.city === (selectedCity ?? '') ? prev : { ...prev, city: selectedCity ?? '' }));
  }, [selectedCity]);

  // Debounce текстового поиска (Enter применяет мгновенно, см. onKeyDown)
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(prev => (prev.q === qInput ? prev : { ...prev, q: qInput }));
    }, 400);
    return () => clearTimeout(t);
  }, [qInput]);

  // Любое изменение фильтров сбрасывает пагинацию (кроме первого рендера)
  const isFirstFiltersRun = useRef(true);
  useEffect(() => {
    if (isFirstFiltersRun.current) { isFirstFiltersRun.current = false; return; }
    setPage(1);
    setAllSpecialists([]);
  }, [search]);

  const firstNewCardRef = useRef<HTMLDivElement | null>(null);
  const firstNewIndexRef = useRef<number>(0);

  // Восстанавливаем список из кеша при навигации (SPA переход)
  useEffect(() => {
    if (allSpecialists.length === 0) {
      const cached = qc.getQueryData<any>(['specialists', search, 1]);
      if (cached?.data?.length) {
        setAllSpecialists(cached.data);
      }
    }
  }, []);
  const qc = useQueryClient();
  const [showRequestForm, setShowRequestForm] = useState(false);

  const router = useRouter();
  const { isAuthenticated } = useAuthStore();

  const handleCreateRequest = () => {
    if (!isAuthenticated()) { router.push('/login'); return; }
    setShowRequestForm(true);
  };

  const { data: stats } = useQuery({
    queryKey: ['stats'],
    queryFn: () => api.get('/stats').then(r => r.data.data),
    staleTime: 5 * 60 * 1000,
  });
  const { user } = useAuthStore();
  const { data: activeBookings } = useQuery({
    queryKey: ['active-bookings-home'],
    queryFn: () => api.get('/bookings').then(r => (r.data.data || []).filter((b: any) => (b.status === 'CONFIRMED' || b.status === 'IN_PROGRESS' || b.status === 'PENDING') && (!b.slot?.startAt || new Date(b.slot.startAt) > new Date()))),
    enabled: !!user,
    staleTime: 60 * 1000,
  });
  const nearestBooking = activeBookings?.find((b: any) => b.slot?.startAt) ?? activeBookings?.[0];
  const activeCount = activeBookings?.length ?? 0;

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get('/categories').then(r => r.data.data),
  });

  const { data: specialistsData, isLoading, isFetching } = useQuery({
    queryKey: ['specialists', search, page],
    queryFn: async () => {
      const r = await api.get('/specialists', {
        params: {
          page,
          limit: 20,
          ...(search.city && { city: search.city }),
          ...(cityLat && cityLng && { user_lat: cityLat, user_lng: cityLng }),
          ...(search.category && { category: search.category }),
          ...(search.date && { date_from: search.date, date_to: search.date }),
          ...(search.q && { search: search.q }),
          ...(search.rating_min && { rating_min: search.rating_min }),
          ...(search.price_min && { price_min: search.price_min }),
          ...(search.price_max && { price_max: search.price_max }),
          ...(search.without_schedule && { without_schedule: 'true' }),
          ...(search.sort !== 'recommended' && { sort: search.sort }),
        }
      });
      return { ...r.data, _page: page };
    },
  });

  useEffect(() => {
    if (!specialistsData) return;
    const newData = specialistsData.data ?? [];
    if (specialistsData._page === 1) {
      setAllSpecialists(newData);
    } else {
      setAllSpecialists(prev => [...prev, ...newData]);
    }
  }, [specialistsData]);
  useEffect(() => {
    if (firstNewIndexRef.current > 0 && firstNewCardRef.current) {
      firstNewCardRef.current.scrollIntoView({ behavior: 'instant' as any, block: 'nearest' });
    }
  }, [allSpecialists.length]);

  // Первая загрузка каталога: ещё нечего показывать — ни счётчик, ни список.
  const isCatalogLoading = (isLoading || isFetching) && allSpecialists.length === 0;

  const total: number = specialistsData?.total ?? specialistsData?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / 20);
  const hasMore = allSpecialists.length < total && total > 0;

  const setFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setSearch(prev => ({ ...prev, [key]: value }));
  };
  const handleReset = () => {
    setQInput('');
    setSearch(EMPTY_FILTERS);
  };
  const hasFilters = !!(search.city || search.category || search.date || search.q || search.rating_min || search.price_min || search.price_max || search.without_schedule);

  // Доп. фильтры (без учёта категории и текстового поиска — у них своё отображение)
  const activeExtraFilters: { key: string; label: string }[] = [
    ...(search.city ? [{ key: 'city', label: search.city }] : []),
    ...(search.date ? [{ key: 'date', label: new Date(search.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }) }] : []),
    ...(search.rating_min ? [{ key: 'rating_min', label: `${search.rating_min}+ ★` }] : []),
    ...(search.price_min || search.price_max ? [{ key: 'price', label: `${search.price_min || '0'}–${search.price_max || '∞'} ₽` }] : []),
    ...(search.without_schedule ? [{ key: 'without_schedule', label: 'Без расписания' }] : []),
  ];
  // Разделители «В вашем городе» / «Рядом». Бэкенд всегда ставит своих первыми
  // (см. specialists.service.ts, сортировка по fromOtherCity), поэтому достаточно
  // найти индекс первого «чужого». Показываем только когда город выбран и в выдаче
  // есть обе группы — иначе заголовок из одного пункта только шумит.
  const firstOtherCityIndex = search.city
    ? allSpecialists.findIndex((s: any) => s.fromOtherCity === true)
    : -1;
  const showCityGroups = firstOtherCityIndex > 0;

  const removeExtraFilter = (key: string) => {
    if (key === 'city') setFilter('city', '');
    else if (key === 'date') setFilter('date', '');
    else if (key === 'rating_min') setFilter('rating_min', '');
    else if (key === 'price') { setSearch(prev => ({ ...prev, price_min: '', price_max: '' })); }
    else if (key === 'without_schedule') setFilter('without_schedule', false);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-4 sm:py-6">
      {/* Анонс активных записей */}
      {user && activeCount > 0 && nearestBooking && (
        <div className="mb-3 flex justify-center">
          <div className="inline-flex items-center gap-2 bg-brand/10 border border-brand/50 rounded-full px-4 py-2 text-sm shadow-sm">
            <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"></span>
            <span className="text-text-primary">
              У вас <span className="text-brand font-semibold">{activeCount}</span> {activeCount === 1 ? 'активная запись' : 'активных записи'} · ближайшая:{' '}
              <span className="text-brand font-medium">
                {nearestBooking.service?.title},{' '}
                {nearestBooking.slot?.startAt
                  ? new Date(nearestBooking.slot.startAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', timeZone: nearestBooking.specialist?.timezone || 'Europe/Moscow' })
                  : ''}{' '}
                {nearestBooking.slot?.startAt
                  ? new Date(nearestBooking.slot.startAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: nearestBooking.specialist?.timezone || 'Europe/Moscow' })
                  : ''}
              </span>
            </span>
            <a href="/dashboard" className="text-brand font-semibold underline underline-offset-2 hover:no-underline flex-shrink-0">Подробнее →</a>
          </div>
        </div>
      )}

      {/* Поиск + фильтры + вид: единая строка высотой 44px */}
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-0">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={qInput}
            onChange={e => setQInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') setFilter('q', qInput); }}
            placeholder="Поиск по имени или услуге..."
            className="w-full h-11 bg-bg-surface border border-border-input rounded-xl pl-9 pr-4 text-sm text-text-body focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all"
          />
        </div>
        <div className="relative flex-shrink-0">
          <button
            type="button"
            onClick={() => setShowFilters(v => !v)}
            aria-expanded={showFilters}
            aria-label="Фильтры"
            className={`relative flex items-center justify-center gap-1.5 h-11 w-11 sm:w-auto sm:px-3.5 rounded-xl border text-sm font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${showFilters || activeExtraFilters.length > 0 ? 'border-brand text-brand bg-brand/5' : 'border-border-input text-gray-500 hover:bg-bg-muted'}`}
          >
            <SlidersHorizontal size={16} />
            <span className="hidden sm:inline">Фильтры</span>
            {activeExtraFilters.length > 0 && (
              <span className="absolute -top-1 -right-1 sm:static inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-brand text-white text-[10px] font-semibold">
                {activeExtraFilters.length}
              </span>
            )}
          </button>
          <FiltersSheet
            open={showFilters}
            onClose={() => setShowFilters(false)}
            city={search.city}
            onCityChange={v => setFilter('city', v)}
            date={search.date}
            onDateChange={v => setFilter('date', v)}
            ratingMin={search.rating_min}
            onRatingMinChange={v => setFilter('rating_min', v)}
            priceMin={search.price_min}
            onPriceMinChange={v => setFilter('price_min', v)}
            priceMax={search.price_max}
            onPriceMaxChange={v => setFilter('price_max', v)}
            withoutSchedule={search.without_schedule}
            onWithoutScheduleChange={v => setFilter('without_schedule', v)}
            onReset={handleReset}
          />
        </div>
        {viewMode === 'list' && (
          <div className="flex h-11 rounded-xl border border-border overflow-hidden flex-shrink-0">
            {CATALOG_VIEW_OPTIONS.map(({ value, label, Icon, mobileOk, desktopOk }) => (
              <button
                key={value}
                type="button"
                onClick={() => handleCatalogViewChange(value)}
                aria-pressed={catalogView === value}
                title={label}
                className={`items-center justify-center h-full w-9 sm:w-10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${mobileOk && desktopOk ? 'flex' : mobileOk ? 'flex sm:hidden' : 'hidden sm:flex'} ${catalogView === value ? 'bg-brand text-white' : 'bg-bg-surface text-text-muted'}`}
              >
                <Icon size={16} />
              </button>
            ))}
          </div>
        )}
        <div className="flex h-11 rounded-xl border border-border overflow-hidden flex-shrink-0">
          <button
            onClick={() => setViewMode('list')}
            aria-pressed={viewMode === 'list'}
            aria-label="Список"
            className={`flex items-center justify-center gap-1.5 h-full w-11 sm:w-auto sm:px-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${viewMode === 'list' ? 'bg-brand text-white' : 'bg-bg-surface text-text-muted'}`}
          >
            <List size={16} /> <span className="hidden sm:inline">Список</span>
          </button>
          <button
            onClick={() => setViewMode('map')}
            aria-pressed={viewMode === 'map'}
            aria-label="Карта"
            className={`flex items-center justify-center gap-1.5 h-full w-11 sm:w-auto sm:px-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${viewMode === 'map' ? 'bg-brand text-white' : 'bg-bg-surface text-text-muted'}`}
          >
            <Map size={16} /> <span className="hidden sm:inline">Карта</span>
          </button>
        </div>
      </div>

      {/* Sticky-панель: только ряд категорий (не съедает экран на мобильном) */}
      <div className="sticky top-14 sm:top-16 z-30 bg-bg-subtle -mx-4 px-4 py-2 mb-3 shadow-[0_1px_0_0_var(--color-border)]">
        <CategoryChips
          categories={categories}
          value={search.category}
          onChange={slug => setFilter('category', slug)}
          facets={specialistsData?.facets}
          facetsTotal={specialistsData?.facetsTotal}
        />
      </div>

      {/* Чипы активных доп. фильтров: тонкая строка, горизонтальный скролл */}
      {activeExtraFilters.length > 0 && (
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4 mb-4 pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {activeExtraFilters.map(f => (
            <button
              key={f.key}
              type="button"
              onClick={() => removeExtraFilter(f.key)}
              className="flex-shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-brand-muted text-brand border border-transparent px-3 py-1.5 text-xs font-medium hover:bg-brand/10 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
            >
              {f.label}
              <X size={12} />
            </button>
          ))}
        </div>
      )}

      {/* Счётчик + сортировка. Счётчик рендерится только когда данные есть:
          иначе в первом кадре и в SSR-HTML отдаётся «Найдено профи: 0».
          Сама строка остаётся, чтобы select сортировки не прыгал. */}
      <div className="flex items-center justify-between gap-3 mb-5 min-h-[1.75rem]">
        {!isCatalogLoading ? (
          <p className="text-gray-500 text-sm font-medium">
            Найдено профи: <span className="text-brand font-bold text-base">{total}</span>
          </p>
        ) : <span />}
        {viewMode === 'list' && (
          <label className="flex items-center gap-2 flex-shrink-0">
            <span className="sr-only">Сортировка</span>
            <select
              value={search.sort}
              onChange={e => setFilter('sort', e.target.value as SortValue)}
              className="h-9 rounded-lg border border-border-input bg-bg-surface px-2.5 text-sm text-text-body focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all"
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {/* Карта */}
      {viewMode === 'map' && (
        <SpecialistsMap specialists={allSpecialists} />
      )}

      {/* Результаты */}
      {viewMode === 'list' && isCatalogLoading ? (
        <div className={`sp-grid ${CATALOG_VIEW_OPTIONS.find(o => o.value === catalogView)?.gridClass ?? ''}`}>
          {[1,2,3,4,5,6].map(i => <SpecialistCardSkeleton key={i} />)}
        </div>
      ) : (
        <>
          {viewMode === 'list' && catalogView === 'focus' && (
            <MobileFocusView specialists={allSpecialists} initialDate={search.date || undefined} />
          )}
          {viewMode === 'list' && catalogView !== 'focus' && (
            <div className={`sp-grid ${CATALOG_VIEW_OPTIONS.find(o => o.value === catalogView)?.gridClass ?? ''}`}>
              {allSpecialists.map((specialist: any, idx: number) => {
                const ref = idx === firstNewIndexRef.current && idx > 0 ? firstNewCardRef : null;
                // Заголовок группы занимает всю ширину сетки, иначе он встанет
                // в колонку и сломает раскладку карточек.
                const header = showCityGroups && (idx === 0 || idx === firstOtherCityIndex) ? (
                  <div className="sp-group-title" style={{ gridColumn: '1 / -1' }}>
                    {idx === 0 ? `В городе ${search.city}` : 'Рядом'}
                  </div>
                ) : null;
                const card = catalogView === 'rows' ? (
                  <div ref={ref}>
                    <SpecialistRow specialist={specialist} />
                  </div>
                ) : (
                  <div ref={ref} style={{ minWidth:0, overflow:'hidden' }}>
                    <SpecialistCard specialist={specialist} initialDate={search.date || undefined} />
                  </div>
                );
                return (
                  <Fragment key={specialist.id}>
                    {header}
                    {card}
                  </Fragment>
                );
              })}
            </div>
          )}
          {/* Кнопка Загрузить ещё */}
          {hasMore && (
            <div className="flex justify-center mt-8">
              <button
                onClick={() => { firstNewIndexRef.current = allSpecialists.length; setPage(p => p + 1); }}
                disabled={isFetching}
                className="px-8 py-3 rounded-xl text-sm font-semibold border-2 border-brand text-brand hover:bg-brand hover:text-white transition-all disabled:opacity-50"
              >
                {isFetching ? 'Загрузка...' : `Загрузить ещё (показано ${allSpecialists.length} из ${total})`}
              </button>
            </div>
          )}
          {/* Блок создания запроса — после всех карточек */}
          {!hasMore && (
            <div className="bg-gradient-to-r from-brand/10 to-brand/5 rounded-2xl border border-brand/20 p-5 mt-8">
              {!showRequestForm ? (
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <h2 className="text-base font-bold text-text-primary mb-0.5">Не нашли подходящего профи?</h2>
                    <p className="text-sm text-text-secondary">Опубликуйте заказ — исполнители сами предложат цену и условия</p>
                  </div>
                  <button onClick={handleCreateRequest}
                    className="flex items-center gap-2 bg-brand text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-brand-dark transition-all shadow-md whitespace-nowrap">
                    <ClipboardList size={16} />
                    Создать заказ
                  </button>
                </div>
              ) : (
                <NewRequestForm onClose={() => setShowRequestForm(false)} onSuccess={() => setShowRequestForm(false)} />
              )}
            </div>
          )}

          {/* Номера страниц */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-1 mt-6 flex-wrap">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  onClick={() => { setPage(p); setAllSpecialists([]); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${p === page ? 'text-white' : 'text-text-secondary hover:bg-bg-muted border border-border'}`}
                  style={p === page ? { backgroundColor: 'var(--color-brand)' } : {}}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
          {!isFetching && allSpecialists.length === 0 && (
            <div className="text-center py-20 bg-bg-surface rounded-2xl border border-border">
              <p className="text-text-secondary text-lg">Профи не найдены</p>
              <p className="text-gray-500 mt-2 text-sm">
                {hasFilters ? 'Попробуйте изменить фильтры или выбрать другую дату' : 'Нет доступных профи'}
              </p>
              {hasFilters && (
                <button onClick={handleReset} className="mt-4 text-brand font-medium hover:underline text-sm">
                  Сбросить фильтры
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
